import {
  FieldValidationResult,
  FieldValidator,
} from '@payaca/types/fieldValidationTypes';
import {
  PaymentSchedule,
  PaymentScheduleStage,
  PaymentScheduleSyncInvoice,
  PaymentScheduleSyncResult,
} from '@payaca/types/payment-schedule';
import { get } from 'lodash-es';

/**
 * Form-bound shape of a {@link PaymentScheduleStage}: while the user is
 * editing, `percentageDue` may still be a string from the input element.
 */
export type EditablePaymentScheduleStage = Omit<
  PaymentScheduleStage,
  'percentageDue'
> & {
  percentageDue: number | string;
};

export type EditablePaymentSchedule = Omit<PaymentSchedule, 'stages'> & {
  stages: EditablePaymentScheduleStage[];
};

export const isPaymentScheduleValid = (
  paymentSchedule: EditablePaymentSchedule
): boolean => {
  if (paymentSchedule.stages.length === 0) {
    // Schedule doesn't have any stages
    return false;
  }

  const percentageSum = paymentSchedule.stages
    .map((stage) => Number(stage.percentageDue))
    .reduce((a, b) => a + b, 0);

  if (percentageSum > 100) {
    // Schedule stage percentages sum to more than 100 (percentage sum can be less than 100)
    return false;
  }

  if (paymentSchedule.stages.some((stage) => !Number(stage.percentageDue))) {
    // Schedule has a stage with no percentage
    return false;
  }

  const numDepositStages = paymentSchedule.stages.filter(
    (stage) => stage.isDeposit
  ).length;

  if (numDepositStages > 1) {
    // Schedule has more than one deposit stage
    return false;
  }

  if (numDepositStages === 1 && paymentSchedule.stages[0].isDeposit !== true) {
    // Schedule has a deposit stage that isn't the first stage
    return false;
  }

  return true;
};

export const getPaymentScheduleStagesWithValues = (
  paymentSchedule: PaymentSchedule,
  projectValue: number
): (PaymentScheduleStage & { value: number })[] => {
  const isValid = isPaymentScheduleValid(paymentSchedule);
  if (!isValid) throw new Error('Invalid payment schedule');

  let total = 0;
  const array: (PaymentScheduleStage & { value: number })[] = [];
  let totalPercentageDue = 0;

  paymentSchedule.stages.forEach((stage) => {
    let value = Math.round((projectValue * stage.percentageDue) / 100);

    if (totalPercentageDue + stage.percentageDue >= 100) {
      // last stage is remaining value
      value = projectValue - total;
    }

    array.push({
      ...stage,
      value,
    });
    total += value;
    totalPercentageDue += stage.percentageDue;
  });

  return array;
};

export const getPaymentScheduleSyncResult = <
  TInvoice extends PaymentScheduleSyncInvoice,
>(
  schedule: PaymentSchedule,
  projectValue: number,
  invoices: TInvoice[]
): PaymentScheduleSyncResult<TInvoice> => {
  let isInSync = true;

  const stagesWithValues = getPaymentScheduleStagesWithValues(
    schedule,
    projectValue
  );

  const totalPercentage = stagesWithValues.reduce(
    (acc, stage) => acc + (stage.percentageDue || 0),
    0
  );

  if (invoices.length > stagesWithValues.length && totalPercentage === 100) {
    isInSync = false;
  }

  const loopLength = Math.max(invoices.length, stagesWithValues.length);
  const stages: PaymentScheduleSyncResult<TInvoice>['stages'] = [];

  for (let i = 0; i < loopLength; i++) {
    const invoice = invoices.length > i ? invoices[i] : null;
    const stage = stagesWithValues.length > i ? stagesWithValues[i] : null;

    let invoicePercentage = 0;

    if (invoice && projectValue) {
      invoicePercentage = ((invoice.totalValue || 0) / projectValue) * 100;
    }

    if (!stage) {
      stages.push({
        stage: null,
        invoice: invoice || null,
        stageValue: null,
        invoiceValue: invoice?.totalValue ?? null,
        invoiceProjectPercentage: invoicePercentage,
      });
      continue;
    }

    if (invoice) {
      if (!!stage.isDeposit !== !!invoice.isDepositInvoice) {
        isInSync = false;
      }
      if (invoice.totalValue !== stage.value) {
        isInSync = false;
      }
    }

    const { value, ...s } = stage;

    stages.push({
      stage: s,
      invoice: invoice || null,
      stageValue: value,
      invoiceValue: invoice?.totalValue ?? null,
      invoiceProjectPercentage: invoicePercentage,
    });
  }

  return { isInSync, stages };
};

export const validateEditablePaymentSchedule = (
  paymentSchedule: EditablePaymentSchedule | null | undefined
): FieldValidationResult => {
  const validationMessages: string[] = [];

  const stagePercentages = paymentSchedule?.stages.map((stage) =>
    Number(stage.percentageDue)
  );

  const scheduleTotalPercentage = stagePercentages?.reduce(
    (acc, curr) => acc + (Number.isFinite(curr) ? curr : 0),
    0
  );

  if (scheduleTotalPercentage && scheduleTotalPercentage > 100) {
    validationMessages.push(
      `Percentages must not add up to more than 100% - currently they total ${scheduleTotalPercentage}%`
    );
  }
  if (stagePercentages?.some((pct) => !Number.isFinite(pct))) {
    validationMessages.push('Payment stage percentage is required');
  }
  if (stagePercentages?.some((pct) => Number.isFinite(pct) && pct <= 0)) {
    validationMessages.push('Payment stage percentage must be more than 0%');
  }
  if (paymentSchedule?.stages.some((stage) => !stage.description)) {
    validationMessages.push('Payment stage description is required');
  }
  const depositStageCount =
    paymentSchedule?.stages.filter((stage) => stage.isDeposit).length ?? 0;
  if (depositStageCount > 1) {
    validationMessages.push('Only one deposit stage is allowed');
  }

  return {
    isValid: !validationMessages.length,
    errors: validationMessages,
  };
};

export const getPaymentScheduleFieldValidator = (): FieldValidator => {
  return (fieldName, formState) =>
    validateEditablePaymentSchedule(
      get(formState, fieldName) as EditablePaymentSchedule | null | undefined
    );
};
